/**
 * Given a template string with formatting markers like `${color}` and a
 * params map, replace the formatting markers with the values from
 * the params map.
 *
 * Additionally, markers can have options. Currently the only one supported
 * is "quote", as an example: `${color;quote}` will result in `"Blue"` being
 * inserted, rather then `Blue`.
 */
export const format = ({ template, args }) => {
  return template.replace(/(\\?\$\{([\w;.-_]+)})/g, (match, expr, attr) => {
    if (expr.startsWith('\\')) return expr.substring(1);
    const [key, opts] = attr.split(';', 2);
    const v = args?.[key];
    if (v === null || v === undefined) return null;
    return opts === 'quote' && typeof v === 'string' ? `"${v}"` : v;
  });
};

const getEnumValue = (uiSchema, k) => {
  const parent = uiSchema.enums.find(e => k.startsWith(e.attribute + '.'));
  if (parent === null || parent === undefined || parent.type !== 'enum') return;
  return parent.values.find(ev => ev.attribute === k);
};

const getEnumValueParam = (uiSchema, path) => {
  const parts = path.split('/');
  if (parts.length !== 3) return;
  const optEnum = uiSchema.enums.find(e => e.attribute === parts[0]);
  const enumValue = optEnum?.values?.find(v => v.attribute === parts[1]);
  return enumValue?.params?.find(p => p.attribute === parts[2]);
};

// Expression ops that invert their operand: an empty list inside one of
// them matches everything.
const NEGATING_OP = /"op":\s*"(not|not_in|not_contains)"/;

// `issues`, when given, collects multi-select values that are no longer in
// the ui schema (e.g. a carrier key re-keyed by a credential change).
// `negated` is true when an enclosing template uses a negating op.
const evaluateEnum = ({
  uiSchema,
  userInput,
  path,
  formatArgs,
  optional,
  issues,
  negated,
}) => {
  const attribute = userInput[path];
  if (!attribute) {
    if (optional === true) return null;
    throw new Error(`UserInput missing path; path: ${path}.`);
  }

  if (Array.isArray(attribute)) {
    const enumValues = attribute.map(a => getEnumValue(uiSchema, a));
    const unmatched = attribute.filter((a, i) => !enumValues[i]);
    if (issues && unmatched.length > 0) {
      issues.push({
        path,
        unmatched,
        total: attribute.length,
        negated: Boolean(negated),
      });
    }
    // Unmatched values are left out, so the list stays valid JSON.
    return format({
      template: enumValues
        .filter(Boolean)
        .map(ev => ev.template.value)
        .toString(),
      args: formatArgs,
    });
  }

  const enumValue = getEnumValue(uiSchema, attribute);
  if (!enumValue)
    throw new Error(
      `EnumValue not found in ui schema; path: ${path}, attribute: ${attribute}.`,
    );
  const updatedPath = `${path}/${enumValue.attribute}`;
  const negatedHere = negated || NEGATING_OP.test(enumValue.template.value);

  const updatedFormatArgs = (enumValue.params ?? []).reduce(
    (formatParams, p) => ({
      ...formatParams,
      [p.attribute]: evaluateParam({
        uiSchema,
        userInput,
        path: `${updatedPath}/${p.attribute}`,
        p,
        formatParams,
        issues,
        negated: negatedHere,
      }),
    }),
    formatArgs,
  );

  return format({
    template: enumValue.template.value,
    args: updatedFormatArgs,
  });
};

const evaluateOptionalParams = ({
  uiSchema,
  userInput,
  path,
  formatArgs,
  optional,
  issues,
  negated,
}) => {
  const attribute = userInput[path];
  if (!attribute || attribute === 'false') return null;

  const param = getEnumValueParam(uiSchema, path);
  if (!param)
    throw new Error(
      `Enum's Value's Param not found in ui schema; path: ${path}.`,
    );
  if (param.type !== 'optional_params')
    throw new Error(
      `Enum's Value's Param is not of type 'opt'; path: ${path}.`,
    );
  const optParams = param;
  const negatedHere = negated || NEGATING_OP.test(optParams.template.value);

  const updatedFormatArgs = (optParams.params ?? []).reduce(
    (formatParams, p) => ({
      ...formatParams,
      [p.attribute]: evaluateParam({
        uiSchema,
        userInput,
        path: `${path}/${p.attribute}`,
        p,
        formatParams,
        issues,
        negated: negatedHere,
      }),
    }),
    formatArgs,
  );

  return format({
    template: optParams.template.value,
    args: updatedFormatArgs,
  });
};

export const evaluateParam = ({
  uiSchema,
  userInput,
  path,
  p,
  formatParams,
  issues,
  negated,
}) => {
  switch (p.type) {
    case 'input.date_time':
    case 'input.number': {
      const v = userInput[path];
      if ((v === null || v === undefined) && !p.optional)
        throw new Error('Required parameter not defined; path: ' + path + '.');
      if (Array.isArray(v))
        throw new Error(`${p.type} should never have array values.`);
      return v;
    }
    case 'input.text': {
      if (Array.isArray(userInput[path]))
        throw new Error(`input.text should never have array values.`);
      const v = JSON.stringify(userInput[path])?.slice(1, -1);
      if ((v === null || v === undefined) && !p.optional)
        throw new Error('Required parameter not defined; path: ' + path + '.');
      return v;
    }
    case 'input.boolean':
      return userInput[path] ? 'true' : 'false';
    case 'let':
      return format({ template: p.template.value, args: formatParams });
    case 'optional_params':
      return evaluateOptionalParams({
        uiSchema,
        userInput,
        path,
        formatArgs: formatParams,
        optional: p.optional,
        issues,
        negated,
      });
    case 'refs': {
      const v = evaluateEnum({
        uiSchema,
        userInput,
        path,
        formatArgs: formatParams,
        optional: p.optional,
        issues,
        negated,
      });
      if ((v === null || v === undefined) && !p.optional)
        throw new Error('Required parameter not defined; path: ' + path + '.');
      return v;
    }
  }
};

export const generateCondition = (uiSchema, userInput, issues) => {
  return evaluateEnum({
    uiSchema,
    userInput,
    path: 'subject',
    formatArgs: {},
    optional: false,
    issues,
  });
};

export const generateResult = (uiSchema, userInput, issues) =>
  evaluateEnum({
    uiSchema,
    userInput,
    path: 'result',
    formatArgs: {},
    optional: false,
    issues,
  });

export const generatePolicyStatement = (uiSchema, userInput, issues) => {
  const condition =
    userInput.conditions.length === 1
      ? generateCondition(uiSchema, userInput.conditions[0], issues)
      : `{"op": "all", "exprs": [${userInput.conditions
          .map(c => generateCondition(uiSchema, c, issues))
          .join(', ')}]}`;
  return `{"condition": ${condition}, "result": ${generateResult(
    uiSchema,
    userInput.result,
    issues,
  )}}`;
};
